import { useState } from 'react';
import { Plus, MoreVertical } from 'lucide-react';
import MonthlyView from './expenses/MonthlyView';
import CategoriesView from './expenses/CategoriesView';
import AnnualView from './expenses/AnnualView';
import TotalsView from './expenses/TotalsView';
import AddExpenseSheet from '../components/expenses/AddExpenseSheet';
import ExpensesDataSheet from '../components/expenses/ExpensesDataSheet';
import { useExpenses } from '../hooks/useExpenses';
import { useYearlyExpenses } from '../hooks/useYearlyExpenses';

const SUB_TABS = [
  { id: 'list', label: 'List' },
  { id: 'categories', label: 'Categories' },
  { id: 'annual', label: 'Annual' },
  { id: 'totals', label: 'Totals' },
];

const ExpensesView = ({ expenseStorageService }) => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [totalsYear, setTotalsYear] = useState(today.getFullYear());
  const [subTab, setSubTab] = useState('list');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(undefined);
  const [dataSheetOpen, setDataSheetOpen] = useState(false);

  const { expenses, income, loading, addExpense, updateExpense, deleteExpense, saveIncome } =
    useExpenses(expenseStorageService, year, month);
  const { expenses: yearlyExpenses, loading: yearlyLoading, refresh: refreshYearly } =
    useYearlyExpenses(expenseStorageService, totalsYear);

  // The Totals tab's yearly data is fetched independently of the monthly
  // view, so mutations need to refresh both — otherwise Totals shows stale
  // data until totalsYear happens to change.
  const handleAddExpense = async (data) => { await addExpense(data); await refreshYearly(); };
  const handleUpdateExpense = async (exp) => { await updateExpense(exp); await refreshYearly(); };
  const handleDeleteExpense = async (id) => { await deleteExpense(id); await refreshYearly(); };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const prevYear = () => setTotalsYear(y => y - 1);
  const nextYear = () => setTotalsYear(y => y + 1);

  const openAdd = () => {
    setEditingExpense(undefined);
    setSheetOpen(true);
  };

  const openEdit = (expense) => {
    setEditingExpense(expense);
    setSheetOpen(true);
  };

  const handleSheetClose = () => {
    setSheetOpen(false);
    setEditingExpense(undefined);
  };

  return (
    <div className="max-w-lg mx-auto space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex bg-gray-100 rounded-lg p-0.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {SUB_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setSubTab(tab.id)}
              className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                subTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setDataSheetOpen(true)}
          className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-50"
          aria-label="Export or import expenses"
        >
          <MoreVertical className="w-5 h-5" />
        </button>
      </div>

      {subTab === 'list' && (
        <MonthlyView
          year={year}
          month={month}
          expenses={expenses}
          income={income}
          loading={loading}
          onPrev={prevMonth}
          onNext={nextMonth}
          onEdit={openEdit}
          onIncomeChange={saveIncome}
        />
      )}
      {subTab === 'categories' && (
        <CategoriesView
          year={year}
          month={month}
          expenses={expenses}
          loading={loading}
          onPrev={prevMonth}
          onNext={nextMonth}
        />
      )}
      {subTab === 'annual' && <AnnualView expenses={expenses} loading={loading} />}
      {subTab === 'totals' && (
        <TotalsView
          year={totalsYear}
          expenses={yearlyExpenses}
          loading={yearlyLoading}
          onPrev={prevYear}
          onNext={nextYear}
        />
      )}

      <button
        onClick={openAdd}
        aria-label="Add expense"
        className="fixed bottom-20 sm:bottom-24 right-4 w-12 h-12 rounded-full bg-accent text-white flex items-center justify-center shadow-card hover:bg-emerald-700 transition-colors z-30"
      >
        <Plus className="w-5 h-5" />
      </button>

      <AddExpenseSheet
        open={sheetOpen}
        expense={editingExpense}
        initialMonth={{ year, month }}
        onSave={handleAddExpense}
        onUpdate={handleUpdateExpense}
        onDelete={handleDeleteExpense}
        onClose={handleSheetClose}
      />

      <ExpensesDataSheet
        open={dataSheetOpen}
        storage={expenseStorageService}
        onClose={() => setDataSheetOpen(false)}
      />
    </div>
  );
};

export default ExpensesView;
